import React, { useState, useEffect } from 'react';
import { AlertCircle, Phone, Mail, User as UserIcon, FileText, Calendar, Video } from 'lucide-react';

const ConsultationDetails = ({ bookingData, onDataUpdate }) => {
  const [formData, setFormData] = useState({
    name: bookingData?.details?.name || '',
    email: bookingData?.details?.email || '',
    phone: bookingData?.details?.phone || '',
    gender: bookingData?.details?.gender || '',
    chiefComplaint: bookingData?.details?.chiefComplaint || '',
    medicalHistory: bookingData?.details?.medicalHistory || '',
    currentMedications: bookingData?.details?.currentMedications || '',
    severity: bookingData?.details?.severity || 'moderate',
    consultationMode: bookingData?.details?.consultationMode || 'in_person',
    reminderPreferences: bookingData?.details?.reminderPreferences || []
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    onDataUpdate({ details: formData });
  }, [formData]);

  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'email':
        // Email is optional, but if provided, must be valid
        if (value && !value?.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          newErrors.email = 'Invalid email format';
        } else {
          delete newErrors?.email;
        }
        break;
      case 'phone':
        if (!value?.match(/^\d{10}$/)) {
          newErrors.phone = 'Phone must be 10 digits';
        } else {
          delete newErrors?.phone;
        }
        break;
      case 'chiefComplaint':
        if (value?.length < 10) {
          newErrors.chiefComplaint = 'Please provide detailed complaint (min 10 characters)';
        } else {
          delete newErrors?.chiefComplaint;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleReminderChange = (preference) => {
    setFormData(prev => ({
      ...prev,
      reminderPreferences: prev?.reminderPreferences?.includes(preference)
        ? prev?.reminderPreferences?.filter(p => p !== preference)
        : [...prev?.reminderPreferences, preference]
    }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-purple-800 mb-2">Patient Information</h2>
        <p className="text-gray-600">Please provide your details for doctor consultation</p>
      </div>
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <UserIcon className="w-4 h-4 inline mr-2" />
            Full Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData?.name}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            placeholder="Enter your full name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="w-4 h-4 inline mr-2" />
            Email (Optional)
          </label>
          <input
            type="email"
            name="email"
            value={formData?.email}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors?.email ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="your@email.com"
          />
          {errors?.email && (
            <p className="text-red-500 text-sm mt-1">{errors?.email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="w-4 h-4 inline mr-2" />
            Phone Number *
          </label>
          <input
            type="tel"
            name="phone"
            value={formData?.phone}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors?.phone ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="10-digit mobile number"
            required
          />
          {errors?.phone && (
            <p className="text-red-500 text-sm mt-1">{errors?.phone}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gender *
          </label>
          <select
            name="gender"
            value={formData?.gender}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      {/* Consultation Mode */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Consultation Mode *
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, consultationMode: 'in_person' }))}
            className={`p-4 border-2 rounded-lg transition-all ${formData?.consultationMode === 'in_person' ? 'border-purple-600 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
              }`}
          >
            <Calendar className="w-6 h-6 mx-auto mb-2 text-purple-600" />
            <p className="font-semibold">In-Person Visit</p>
            <p className="text-sm text-gray-600">Visit our clinic</p>
          </button>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, consultationMode: 'virtual' }))}
            className={`p-4 border-2 rounded-lg transition-all ${formData?.consultationMode === 'virtual' ? 'border-purple-600 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
              }`}
          >
            <Video className="w-6 h-6 mx-auto mb-2 text-purple-600" />
            <p className="font-semibold">Virtual Consultation</p>
            <p className="text-sm text-gray-600">Video call</p>
          </button>
        </div>
      </div>
      {/* Medical Information */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <FileText className="w-4 h-4 inline mr-2" />
          Chief Complaint *
        </label>
        <textarea
          name="chiefComplaint"
          value={formData?.chiefComplaint}
          onChange={handleChange}
          rows="3"
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 ${errors?.chiefComplaint ? 'border-red-500' : 'border-gray-300'
            }`}
          placeholder="Describe your main health concern in detail..."
          required
        />
        {errors?.chiefComplaint && (
          <p className="text-red-500 text-sm mt-1">{errors?.chiefComplaint}</p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Severity Level
        </label>
        <div className="flex gap-2">
          {['mild', 'moderate', 'severe']?.map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, severity: level }))}
              className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${formData?.severity === level
                ? 'border-purple-600 bg-purple-50 text-purple-600' : 'border-gray-300 hover:border-purple-400'
                }`}
            >
              {level?.charAt(0)?.toUpperCase() + level?.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Medical History (Optional)
        </label>
        <textarea
          name="medicalHistory"
          value={formData?.medicalHistory}
          onChange={handleChange}
          rows="2"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          placeholder="Any previous medical conditions, surgeries, allergies..."
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Current Medications (Optional)
        </label>
        <textarea
          name="currentMedications"
          value={formData?.currentMedications}
          onChange={handleChange}
          rows="2"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          placeholder="List any medications you are currently taking..."
        />
      </div>
      {/* Reminder Preferences */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Reminder Preferences
        </label>
        <div className="flex flex-wrap gap-2">
          {['email', 'sms', 'whatsapp']?.map((pref) => (
            <button
              key={pref}
              type="button"
              onClick={() => handleReminderChange(pref)}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${formData?.reminderPreferences?.includes(pref)
                ? 'border-purple-600 bg-purple-50 text-purple-600' : 'border-gray-300 hover:border-purple-400'
                }`}
            >
              {pref?.charAt(0)?.toUpperCase() + pref?.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {/* Info Box */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-semibold mb-1">Important Information:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Please provide accurate medical information for proper consultation</li>
              <li>Consultation fee will be displayed after doctor selection</li>
              <li>You will receive appointment confirmation via your selected channels</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationDetails;